import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Circle from 'ol/style/Circle';
import RegularShape from 'ol/style/RegularShape';

import { Stroke } from 'cadenza/map/style/stroke';
import { degreesToRadians } from 'cadenza/utils/math';
import { RoundedSquare } from 'cadenza/map/style/rounded-square';
import { Diamond } from 'cadenza/map/style/diamond';
import { getColorWithOpacity, shadeOf } from 'cadenza/utils/colors/colors';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { routeWayPointStyleProvider } from 'cadenza/map/style/style-providers/route-way-point';
import { routeStepLineStyleProvider } from 'cadenza/map/style/style-providers/route-line-geometry';
import { getSize } from 'cadenza/map/style/style-util';
import { createLineOlStyle, createStrokeForLine } from 'cadenza/map/style/create-line-style';
import { getOvalRenderer } from 'cadenza/map/geom/oval-renderer';
import { FillSignatureType, PointSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import { createCanvasPattern, createCanvasWithImagePattern } from 'cadenza/map/style/style-canvas-util';
import { FillStyle } from 'cadenza/model/fill-style';

export const DEFAULT_OL_Z_INDEX = 10;

/**
 * @callback styleProvider
 * @param {object} style - A style DTO
 * @param {GeometryType} geometryType - A feature geometry type
 * @param {number} mapPixelRatio - The map pixel ratio
 * @return {ol.style.Style|Function} Return a style object if the provider support the given style, or undefined otherwise.
 */

/**
 * Specific style providers registry.
 *
 * @type {styleProvider[]}
 */
export const STYLE_PROVIDERS = [
  routeWayPointStyleProvider,
  routeStepLineStyleProvider
];

/**
 * Creates an OL style object from a style DTO
 *
 * @param {object} style - A style DTO
 * @param {object} options - optional parameters
 * @param {GeometryType} options.geometryType - The geometry type for which to create the OL Style
 * @return {(ol.style.Style|ol.style.Style[]|Function)} An OL style object or an array of style objects
 * @throws Throws an error if the DTO does not have a pointSignatureType property
 *     (only point styles are supported right now)
 */
export function createStyle (
  style,
  { geometryType = GeometryType.POINT } = {},
  mapPixelRatio = window.devicePixelRatio
) {
  const olStyle = createOlStyleForGeometryType(style, geometryType, mapPixelRatio);
  olStyle.hashCode = style.hashCode;
  return olStyle;
}

function createOlStyleForGeometryType (style, geometryType, mapPixelRatio) {
  // resolve to a specific style
  const externalStyle = STYLE_PROVIDERS
    .map(styleProvider => styleProvider(style, geometryType, mapPixelRatio))
    .find(providedStyle => !!providedStyle);
  if (externalStyle) {
    return externalStyle;
  }
  // resolve to a standard style
  switch (geometryType) {
    case GeometryType.POINT:
    case GeometryType.MULTI_POINT:
      return createPointOlStyle(style);
    case GeometryType.LINE:
    case GeometryType.LINE_STRING:
    case GeometryType.MULTI_LINE_STRING:
      return createLineOlStyle(style);
    case GeometryType.OVAL:
      return createOvalOlStyle(style);
    default:
      return createPolygonOlStyle(style);
  }
}

function createPointOlStyle (style) {
  switch (style.pointSignatureType) {
    case PointSignatureType.CIRCLE:
      return createCircle(style);
    case PointSignatureType.DIAMOND:
      return createDiamond(style);
    case PointSignatureType.TRIANGLE:
      return createTriangle(style);
    case PointSignatureType.SQUARE:
      return createSquare(style);
    case PointSignatureType.ROUNDED_SQUARE:
      return createRoundedSquare(style);
    case PointSignatureType.IMAGE:
      return createImage(style);
    case PointSignatureType.CHARACTER:
      return createCharacterImage(style);
    default:
      throw new Error(`Cannot create OL point style for an unknown point signature type '${style.pointSignatureType}'`);
  }
}

function createOvalOlStyle (style) {
  const olStyle = createPolygonOlStyle(style);
  olStyle.setRenderer(getOvalRenderer());
  return olStyle;
}

function createPolygonOlStyle (style) {
  // In case of a polygon, the borderColor is the foreground color for the line
  // Since borderColor has implicit alpha value,
  // we have to reset the foreground color opacity
  const strokeStyle = Object.assign({}, style, {
    [StyleProperty.FOREGROUND_COLOR]: style.borderColor,
    [StyleProperty.FOREGROUND_COLOR_OPACITY]: undefined
  });
  return new Style({
    fill: createFill(style),
    stroke: createStrokeForLine(strokeStyle),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createFill (style) {
  const color = createFillColorOrPattern(style);
  return color ? new Fill({ color }) : undefined;
}

export function createFillColorOrPattern (style) {
  const isFilled = style[StyleProperty.IS_FILLED];
  const fillStyle = style[StyleProperty.FILL_STYLE];
  const foregroundColor = style[StyleProperty.FOREGROUND_COLOR];
  const backgroundColor = style[StyleProperty.BACKGROUND_COLOR];

  if (isFilled === false || fillStyle === 'TRANSPARENT') {
    return undefined;
  }

  if (style[StyleProperty.FILL_SIGNATURE_TYPE] === FillSignatureType.IMAGED) {
    return createCanvasWithImagePattern(style[StyleProperty.IMAGE], style[StyleProperty.ROTATION]);
  }

  if (fillStyle !== 'SOLID' && FillStyle.values.includes(fillStyle)) {
    return createCanvasPattern(fillStyle, foregroundColor, backgroundColor);
  }

  return createForegroundColor(style);
}

function createForegroundColor (style) {
  if (style.foregroundColor && typeof style.foregroundColorOpacity === 'number') {
    return getColorWithOpacity(style.foregroundColor, style.foregroundColorOpacity);
  }
  return style.foregroundColor;
}

function createStroke (style) {
  // compute the color if requested
  const borderColor = style.borderColorComputed
    ? shadeOf(style.foregroundColor)
    : style.borderColor;
  if (style.lineWidth && style.lineWidth > 0) {
    return new Stroke({
      color: borderColor,
      width: getSize(style.lineWidth, style.lineWidthUnit)
    });
  } else {
    return null;
  }
}

function createCircle (style) {
  return new Style({
    image: new Circle({
      fill: createFill(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      stroke: createStroke(style)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createDiamond (style) {
  return new Style({
    image: new Diamond({
      fill: createFill(style),
      stroke: createStroke(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createTriangle (style) {
  return new Style({
    image: new RegularShape({
      fill: createFill(style),
      stroke: createStroke(style),
      points: 3,
      radius: getSize(style.symbolSize, style.symbolSizeUnit) * 0.66,
      angle: 0,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createSquare (style) {
  return new Style({
    image: new RegularShape({
      fill: createFill(style),
      stroke: createStroke(style),
      points: 4,
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / Math.sqrt(2),
      angle: degreesToRadians(45),
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createRoundedSquare (style) {
  return new Style({
    image: new RoundedSquare({
      fill: createFill(style),
      stroke: createStroke(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

/**
 * @EpicSplit: Cleanup symbolSize usage in this method, currently there's a few places who might need this method:
 * feature-geometry-editor, classic-sketch, workbook-sketch, feature-style-resolve.js, timeline-api.ts
 * It seems the timeline-api is the only one where symbolSize can be different than 1, but further checks are needed.
 * It also seems like OL makes no difference between "scale: undefined" and "scale: 1" so if we fix/don't need to support
 * the timeline case we may remove the symbolSize from here and either keep imageScale only or better even introduce
 * width and height instead of scale since this is more flexible.
 *
 * @param style {Object} and object containing the style properties.
 * @returns {Style} an OL Style object
 */
function createImage (style) {
  const scale = style.imageScale ?? style.symbolSize;
  return new Style({
    image: new Icon({
      src: style[StyleProperty.IMAGE_AS_BASE64],
      rotation: style.rotation ? degreesToRadians(style.rotation) : undefined,
      scale: scale != null ? scale : undefined // null and undefined is treated differently by OL
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

// @EpicSplit: Cleanup symbolSize because it's used only for character rendering which is configurable only for classical layers.
// And we don't have this configuration possibility in workbooks for data view layers
function createCharacterImage (style) {
  return new Style({
    image: new Icon({
      src: style[StyleProperty.IMAGE_AS_BASE64],
      rotation: style.rotation ? degreesToRadians(style.rotation) : undefined,
      scale: style.symbolSize || 1
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}
